import React, {useCallback, useEffect, useState} from "react";
import {useIntegrationsContext} from "../../api/integrations/IntegrationsContext";
import {useHistory, useParams} from "react-router";
import {showError} from "../../utils/NotificationUtils";
import {Loader} from "../ui/Loader";
import {GetConnectionsListProps} from "../../api/integrations/IntegrationsDTO";
import {AppButton} from "../ui/AppButton";
import {makeStyles} from "@mui/styles";
import ReplayIcon from "@mui/icons-material/Replay";
import IconButton from "@mui/material/IconButton/IconButton";
import {AppAutoComplete} from "../ui/AppAutoComplete";
import {toast} from "react-toastify";

const useStyles = makeStyles(() => ({
    root: {},
    content: {
        padding: 10,
    },
    footer: {
        borderTop: "1px solid #D7D7D7",
        display: "flex",
        justifyContent: "space-between",
        padding: "10px",
        gap: 20,
        alignItems: "center",
    },
}));

interface Props {
    readonly isOAuth2: boolean;
}

export function AuthTest({ isOAuth2}: Props) {
    const classes = useStyles();
    const history = useHistory();
    const [update, setUpdate] = useState<number>(0);
    const [data, setData] = useState<GetConnectionsListProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [signInLoading, setSigInLoading] = useState(false);
    const [currentAcc, setCurrentAcc] = useState<number | null>();
    const [testing, setTesting] = useState(false);
    const {IntegrationsApi} = useIntegrationsContext();
    const params: any = useParams();
    let createConnectionWindow: Window | null;

    const getConnectionsList = useCallback(() => {
        IntegrationsApi.getConnectionsList({params: {integrationId: params.id}})
            .then((res) => {
                setData(res.payload.list);
                setCurrentAcc(res.payload.list[res.payload.list?.length - 1]?.id);
                setLoading(false);
            })
            .catch((err) => {
                showError(err);
                setLoading(false);
            });
    }, [IntegrationsApi, params, update]);

    useEffect(() => {
        let isUnmount = false;
        if (!isUnmount) {
            getConnectionsList();
        }
        return () => {
            isUnmount = true;
        };
    }, [IntegrationsApi, params, update]);

    useEffect(() => {
        const windowGetMessage = (event: any) => {
            if (event.data === "cancel") {
                // @ts-ignore
                createConnectionWindow.close();
            } else if (event.data === "success") {
                // @ts-ignore
               toast.success("Connection successfully created");
                setUpdate(prev => prev + 1)
            }
        };

        window.addEventListener("message", windowGetMessage);

        return () => {
            window.removeEventListener("message", windowGetMessage)
        }
    }, []);

    const handleTest = useCallback(
        (id) => {
            setTesting(true);
            IntegrationsApi.testAuth({
                data: {
                    authAccountId: id,
                    objectId: params.authId,
                },
            })
                .then(() => {
                    setTesting(false);
                    toast.success("Test passed")
                })
                .catch((err) => {
                    showError(err);
                    setTesting(false);
                });
        },
        [IntegrationsApi, params]
    );

    const handleSignIn = useCallback(() => {
        if (isOAuth2) {
            setSigInLoading(true);
            IntegrationsApi.startOAuth2({params: {integrationId: params.id}})
                .then((res) => {
                    window.open(
                        res.payload.replace("connector", "connector"),
                        "OAuth2",
                        "left=100,top=100,width=500,height=500"
                    );
                    setSigInLoading(false);
                })
                .catch((err) => {
                    showError(err);
                    setSigInLoading(false);
                });
        } else {
            // history.push("/connection/" + params.id);
            createConnectionWindow = window.open(
                "/connection/" + params.id,
                "integration",
                "left=100,top=100,width=500,height=500"
            );

        }
    }, [IntegrationsApi, history, params]);

    return (
        <div className={classes.root}>
            {loading ? (
                <Loader/>
            ) : (
                <>
                    <div className={classes.content}>
                        <IconButton onClick={getConnectionsList}>
                            <ReplayIcon/>
                        </IconButton>
                        {data.length > 0 ? (
                            <>
                                <AppAutoComplete
                                    fullWidth={true}
                                    label="Select account"
                                    onValueChange={(value) => setCurrentAcc(Number(value))}
                                    options={data.map((item) => ({
                                        label: item.name + " " + item.id,
                                        value: String(item.id),
                                    }))}
                                />
                                <AppButton sx={{mt: 2}} onClick={handleSignIn}>
                                    Add new connection
                                </AppButton>
                            </>
                        ) : (
                            <AppButton
                                loading={signInLoading}
                                disabled={signInLoading}
                                fullWidth
                                onClick={handleSignIn}
                            >
                                Sign In
                            </AppButton>
                        )}
                    </div>
                    <div className={classes.footer}>
            <span>
              Run your Test request using the account you connected to make sure
              that everything is working.{" "}
            </span>
                        <AppButton
                            loading={testing}
                            disabled={testing}
                            onClick={() => handleTest(currentAcc)}
                        >
                            Test Auth
                        </AppButton>
                    </div>
                </>
            )}
        </div>
    );
}
